import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config';
import { useSocket } from '../../../contexts/SocketContext';
import type { TestListItem } from '../../../types/test-types';
import type { ConsoleOutputEntry } from '../ConsoleOutput';

interface UseTestExecutionOptions {
  specId: string | undefined;
  onTestsUpdated: () => void;
  setTests: Dispatch<SetStateAction<TestListItem[]>>;
}

interface TestProgress {
  completed: number;
  total: number;
}

interface UseTestExecutionResult {
  runningTests: boolean;
  runConsoleOutput: ConsoleOutputEntry[];
  testProgress: TestProgress | null;
  handleRunTests: (filenames?: string[]) => Promise<void>;
  handleStopTestExecution: () => Promise<void>;
}

interface SelectedRunState {
  filenames: Set<string>;
  total: number;
  completed: number;
}

export function useTestExecution({
  specId,
  onTestsUpdated,
  setTests,
}: UseTestExecutionOptions): UseTestExecutionResult {
  const [runningTests, setRunningTests] = useState(false);
  const [runConsoleOutput, setRunConsoleOutput] = useState<ConsoleOutputEntry[]>([]);
  const [testProgress, setTestProgress] = useState<TestProgress | null>(null);
  const { addEventListener } = useSocket();

  // Stable refs for callbacks used inside event handlers
  const specIdRef = useRef(specId);
  specIdRef.current = specId;

  const onTestsUpdatedRef = useRef(onTestsUpdated);
  onTestsUpdatedRef.current = onTestsUpdated;

  const setTestsRef = useRef(setTests);
  setTestsRef.current = setTests;

  // Track "Run Selected" state so single-test events can update progress
  const selectedRunRef = useRef<SelectedRunState | null>(null);

  useEffect(() => {
    const applyTestUpdate = (filename: string, status: string, result: string | null | undefined) => {
      setTestsRef.current((prev) =>
        prev.map((t) =>
          t.filename === filename
            ? {
                ...t,
                status: status as TestListItem['status'],
                result: (result ?? undefined) as TestListItem['result'],
              }
            : t,
        ),
      );
    };

    const registerExecutionHandlers = (prefix: string, label: string) => {
      return [
        addEventListener(`${prefix}-start`, (data: { schemaId: string }) => {
          if (data.schemaId !== specIdRef.current) return;
          setRunConsoleOutput([]);
          setTestProgress(null);
          onTestsUpdatedRef.current();
        }),
        addEventListener(`${prefix}-output`, (data: { schemaId: string; type: string; data: string }) => {
          if (data.schemaId !== specIdRef.current) return;
          setRunConsoleOutput((prev) => [...prev, { type: data.type, data: data.data }]);
        }),
        addEventListener(`${prefix}-complete`, (data: { schemaId: string; success: boolean }) => {
          if (data.schemaId !== specIdRef.current) return;
          setRunningTests(false);
          setTestProgress(null);
          onTestsUpdatedRef.current();
          if (data.success) {
            toast.success(`${label} completed successfully`);
          } else {
            toast.error(`${label} failed`);
          }
        }),
        addEventListener(`${prefix}-error`, (data: { schemaId: string; error: string }) => {
          if (data.schemaId !== specIdRef.current) return;
          setRunningTests(false);
          setTestProgress(null);
          setRunConsoleOutput((prev) => [...prev, { type: 'stderr', data: `Error: ${data.error}` }]);
          toast.error(`${label} failed`);
        }),
      ];
    };

    const cleanups = [
      ...registerExecutionHandlers('test-execution', 'Test execution'),
      ...registerExecutionHandlers('test-execution-improved', 'Test execution with improvements'),

      addEventListener('test-progress-init', (data: { schemaId: string; totalTests: number }) => {
        if (data.schemaId !== specIdRef.current) return;
        setTestProgress({ completed: 0, total: data.totalTests });
      }),

      addEventListener('test-progress-complete', (data: { schemaId: string; totalTests: number }) => {
        if (data.schemaId !== specIdRef.current) return;
        setTestProgress({ completed: data.totalTests, total: data.totalTests });
      }),

      addEventListener(
        'test-result-update',
        (data: {
          schemaId: string;
          filename: string;
          result: string;
          status: string;
          completedCount: number;
          totalCount: number;
        }) => {
          if (data.schemaId !== specIdRef.current) return;
          setTestProgress({ completed: data.completedCount, total: data.totalCount });
          applyTestUpdate(data.filename, data.status, data.result);
        },
      ),

      // Stream single-test output during "Run Selected" mode
      addEventListener(
        'single-test-output',
        (data: { schemaId: string; filename: string; type: string; data: string }) => {
          if (data.schemaId !== specIdRef.current) return;
          if (!selectedRunRef.current) return;
          if (!selectedRunRef.current.filenames.has(data.filename)) return;
          const line = data.data;
          const prefix = selectedRunRef.current.total > 1 && line.trim().length > 0 ? `[${data.filename}] ` : '';
          setRunConsoleOutput((prev) => [...prev, { type: data.type, data: `${prefix}${line}` }]);
        },
      ),

      // Track single-test completion during "Run Selected" mode
      addEventListener(
        'single-test-complete',
        (data: { schemaId: string; filename: string; success: boolean; result: string | null; status?: string }) => {
          if (data.schemaId !== specIdRef.current) return;
          const run = selectedRunRef.current;
          if (!run) return;
          if (!run.filenames.has(data.filename)) return;

          run.completed += 1;
          setTestProgress({ completed: run.completed, total: run.total });

          if (data.status) {
            applyTestUpdate(data.filename, data.status, data.result);
          }

          if (run.completed >= run.total) {
            selectedRunRef.current = null;
            setRunningTests(false);
            onTestsUpdatedRef.current();
            toast.success('Selected tests completed');
          }
        },
      ),
    ];

    return () => {
      for (const cleanup of cleanups) cleanup();
    };
  }, [addEventListener]);

  const handleRunSelected = useCallback(
    async (filenames: string[]) => {
      if (!specId) {
        toast.error('No active specification selected');
        return;
      }

      setRunningTests(true);
      setRunConsoleOutput([{ type: 'stdout', data: `Running ${filenames.length} selected test(s)...\n` }]);
      setTestProgress({ completed: 0, total: filenames.length });
      selectedRunRef.current = {
        filenames: new Set(filenames),
        total: filenames.length,
        completed: 0,
      };

      // Immediately show selected tests as "running" in the UI
      const filenameSet = new Set(filenames);
      setTestsRef.current((prev) =>
        prev.map((t) => (filenameSet.has(t.filename) ? { ...t, status: 'running' as TestListItem['status'] } : t)),
      );

      const results = await Promise.allSettled(
        filenames.map(async (filename) => {
          const response = await fetch(
            `${API_BASE_URL}/api/generate/run-single-test/${specId}/${encodeURIComponent(filename)}`,
            { method: 'POST', headers: { 'Content-Type': 'application/json' } },
          );
          const data = await response.json();
          if (!response.ok || !data.success) {
            setRunConsoleOutput((prev) => [...prev, { type: 'stderr', data: `Failed to start: ${filename}\n` }]);
            return false;
          }
          return true;
        }),
      );
      const startedCount = results.filter((r) => r.status === 'fulfilled' && r.value).length;

      // If no tests could be started, reset immediately
      if (startedCount === 0) {
        selectedRunRef.current = null;
        setRunningTests(false);
        setTestProgress(null);
        toast.error('Failed to start any tests');
      }
    },
    [specId],
  );

  const handleRunTests = useCallback(
    async (filenames?: string[]) => {
      if (filenames && filenames.length > 0) {
        return handleRunSelected(filenames);
      }

      if (!specId) {
        toast.error('No active specification selected');
        return;
      }

      setRunningTests(true);
      setRunConsoleOutput([{ type: 'stdout', data: 'Starting test execution with improvements...\n' }]);

      try {
        const url = `${API_BASE_URL}/api/generate/run-tests-with-improvements/${specId}`;

        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          setRunningTests(false);
          setRunConsoleOutput([
            { type: 'stderr', data: `Error: ${data.message || 'Failed to start test execution with improvements'}` },
          ]);
          toast.error('Failed to start test execution with improvements');
        } else {
          toast.success('Test execution with improvements started');
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('[TestExec] Error starting test execution with improvements:', error);
        setRunningTests(false);
        setRunConsoleOutput([{ type: 'stderr', data: `Error: ${message}` }]);
        toast.error('Failed to start test execution with improvements');
      }
    },
    [specId, handleRunSelected],
  );

  const handleStopTestExecution = useCallback(async () => {
    if (!specId) return;

    // If running selected tests, stop each individual test
    const run = selectedRunRef.current;
    if (run) {
      const stopResults = await Promise.allSettled(
        Array.from(run.filenames).map(async (filename) => {
          const url = `${API_BASE_URL}/api/generate/run-single-test/${specId}/${encodeURIComponent(filename)}/stop`;
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
          const data = await response.json();
          return response.ok && data.success;
        }),
      );
      const stopped = stopResults.filter((r) => r.status === 'fulfilled' && r.value).length;
      selectedRunRef.current = null;
      setRunningTests(false);
      setTestProgress(null);
      setRunConsoleOutput((prev) => [...prev, { type: 'stderr', data: '\n[Execution stopped by user]\n' }]);
      toast.success(`Stopped ${stopped} test(s)`);
      onTestsUpdatedRef.current();
      return;
    }

    // Otherwise stop batch execution
    try {
      const url = `${API_BASE_URL}/api/generate/run-tests/${specId}/stop`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Test execution stopped');
        setRunningTests(false);
        setRunConsoleOutput((prev) => [...prev, { type: 'stderr', data: '\n[Process stopped by user]\n' }]);
      } else {
        toast.error(data.error || 'Failed to stop test execution');
      }
    } catch (error: unknown) {
      console.error('[TestExec] Error stopping test execution:', error);
      toast.error('Failed to stop test execution');
    }
  }, [specId]);

  return {
    runningTests,
    runConsoleOutput,
    testProgress,
    handleRunTests,
    handleStopTestExecution,
  };
}
